<template>
    <div class="fanout-view-wrap">
        <!-- Kennzahlen -->
        <v-row class="mt-1">
            <v-col v-for="kpi in kpiCards" :key="kpi.key" cols="12" sm="6" md="3" class="d-flex">
                <v-card class="rounded_card quaroshadow pa-4 fq-kpi flex-grow-1">
                    <div class="fq-kpi__head">
                        <span class="fq-kpi__icon"
                            ><v-icon small color="primary">{{ kpi.icon }}</v-icon></span
                        >
                        <span class="fq-kpi__label">
                            {{ kpi.label }}
                            <v-tooltip top max-width="320">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>{{ kpi.tooltip }}</span>
                            </v-tooltip>
                        </span>
                    </div>
                    <div class="fq-kpi__value">
                        {{ kpi.value }}
                        <span v-if="kpi.total" class="fq-kpi__total">of {{ kpi.total }}</span>
                    </div>
                    <!-- Anteil als Balken; leerer Platzhalter hält alle Karten gleich hoch -->
                    <div class="fq-kpi__bar-row">
                        <template v-if="kpi.share != null">
                            <v-progress-linear :value="kpi.share" color="primary" background-color="grey lighten-3" rounded height="4" class="fq-kpi__bar" />
                            <span class="fq-kpi__share">{{ kpi.share }}%</span>
                        </template>
                        <span v-else class="fq-kpi__hint">{{ kpi.hint }}</span>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Keyword-Auswertung über alle Fanout-Queries des Zeitraums -->
        <v-card class="rounded_card quaroshadow fq-card mt-5">
            <v-toolbar flat class="gradient-toolbar elevation-0">
                <div class="d-flex align-center">
                    <h2 class="project-data-heading__title">What the Models Search For</h2>
                    <v-tooltip top max-width="360">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>
                            Individual words across all distinct queries of this range. The number is how many <em>queries</em> contain the term — a term repeated inside one query still counts once.
                            Common filler words and search operators (site:, inurl:) are removed; brand names and commercial words like "beste" are kept on purpose, because it is telling when a model
                            adds them by itself.
                        </span>
                    </v-tooltip>
                </div>
                <v-spacer />
                <v-btn rounded color="primary" :disabled="!queryKeywords.length" @click="exportKeywords" aria-label="Export keywords">
                    <v-icon>mdi-cloud-download</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="fq-card__body">
                <div class="fq-section__subtitle mb-3">Most frequent terms across all fanout queries — click a term to filter the table below</div>
                <div v-if="loadingQueries && !queryKeywords.length" class="fq-empty">Loading…</div>
                <div v-else-if="!queryKeywords.length" class="fq-empty">No fanout queries in this range</div>
                <div v-else>
                    <v-chip
                        v-for="kw in visibleKeywords"
                        :key="kw.term"
                        :class="['fq-kw-chip', keywordTier(kw)]"
                        :color="activeKeyword === kw.term ? 'primary' : 'teal lighten-5'"
                        :text-color="activeKeyword === kw.term ? 'white' : 'teal darken-2'"
                        small
                        @click="toggleKeyword(kw.term)">
                        {{ kw.term }}
                        <span class="fq-kw-chip__count">{{ kw.queryCount }}</span>
                        <v-icon v-if="activeKeyword === kw.term" x-small right>mdi-close</v-icon>
                    </v-chip>
                    <v-btn v-if="queryKeywords.length > 24" text x-small color="primary" class="ml-1" @click="keywordsExpanded = !keywordsExpanded">
                        {{ keywordsExpanded ? "Show less" : `Show all ${queryKeywords.length}` }}
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>

        <!-- Aggregierte Query-Liste (eine Zeile = eine Query, nicht ein Response) -->
        <v-card class="rounded_card quaroshadow fq-card mt-5">
            <v-toolbar flat class="gradient-toolbar elevation-0">
                <div class="d-flex align-center fq-toolbar__title">
                    <h2 class="project-data-heading__title">Fanout Queries</h2>
                    <v-tooltip top max-width="340">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>{{ queriesSubtitle }}</span>
                    </v-tooltip>
                </div>
                <v-spacer />

                <v-text-field
                    v-model="querySearch"
                    class="fq-table-search mr-3"
                    rounded
                    dense
                    filled
                    clearable
                    clear-icon="mdi-close"
                    prepend-inner-icon="mdi-magnify"
                    label="Search queries..."
                    single-line
                    hide-details />
                <v-btn rounded color="primary" :disabled="!queriesTotal" :loading="exportingQueries" @click="exportQueries" aria-label="Export queries">
                    <v-icon>mdi-cloud-download</v-icon>
                </v-btn>
            </v-toolbar>
            <v-chip v-if="activeKeyword" small close color="primary" text-color="white" class="ml-3 mr-3 mt-3" @click:close="toggleKeyword(activeKeyword)">
                <v-icon x-small left>mdi-filter</v-icon>
                {{ activeKeyword }}
            </v-chip>
            <!-- <v-chip-group v-model="quickFilter" mandatory class="fq-quick-filter mr-3" @change="onQuickFilterChange">
                <v-chip v-for="f in quickFilters" :key="f.value" :value="f.value" small filter outlined :color="quickFilter === f.value ? 'primary' : ''">
                    {{ f.label }}
                </v-chip>
            </v-chip-group> -->
            <v-data-table
                :headers="queryHeaders"
                :items="queries"
                :loading="loadingQueries"
                :server-items-length="queriesTotal"
                :options.sync="queryOptions"
                :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                class="elevation-0 fq-query-table"
                @click:row="openFanoutQueryPanel">
                <!-- Spalten-Tooltips aus der Header-Definition (queryHeaders[].tooltip) -->
                <template v-for="h in queryHeaders.filter((x) => x.tooltip)" v-slot:[`header.${h.value}`]="{ header }">
                    <span :key="h.value" class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="320">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>{{ h.tooltip }}</span>
                        </v-tooltip>
                    </span>
                </template>
                <template v-slot:[`item.query`]="{ item }">
                    <span class="d-flex align-center">
                        <v-icon x-small :color="item.hasSiteOperator ? 'primary' : 'grey lighten-1'" class="mr-2">{{ item.hasSiteOperator ? "mdi-web" : "mdi-magnify" }}</v-icon>
                        <span class="fq-query-cell" :title="item.query">{{ item.query }}</span>
                    </span>
                </template>
                <template v-slot:[`item.frequency`]="{ item }">
                    <div v-if="item.frequency != null" class="d-flex align-center justify-end">
                        <span class="fq-freq-value mr-2">{{ item.frequency }}%</span>
                        <v-progress-linear :value="item.frequency" :color="frequencyColor(item.frequency)" background-color="grey lighten-3" rounded height="5" style="max-width: 56px" />
                    </div>
                    <span v-else>–</span>
                </template>
                <template v-slot:[`item.prompts`]="{ item }">
                    <span class="fq-prompt-cell" :title="(item.prompts || []).join(' · ')">{{ (item.prompts || [])[0] || "–" }}</span>
                    <span v-if="(item.prompts || []).length > 1" class="grey--text"> +{{ item.prompts.length - 1 }}</span>
                </template>
                <template v-slot:[`item.dayCount`]="{ item }">
                    <span :class="['fq-days', item.dayCount > 1 ? 'fq-days--multi' : '']">{{ item.dayCount }}d</span>
                </template>
                <template v-slot:[`item.lastSeen`]="{ item }">
                    <div class="fq-date-cell">Last: {{ formatDate(item.lastSeen) }}</div>
                    <div class="fq-date-cell grey--text">First: {{ formatDate(item.firstSeen) }}</div>
                </template>
            </v-data-table>
        </v-card>

        <!-- Query-Detail-Panel (rechts, Hülle .shopping-item-dialog ist global definiert) -->
        <v-dialog v-model="fanoutPanelOpen" max-width="520" transition="slide-x-reverse-transition" content-class="shopping-item-dialog" overlay-opacity="0.35">
            <v-card v-if="selectedFanoutQuery" class="fq-panel-card">
                <v-toolbar flat class="fq-panel-toolbar">
                    <v-toolbar-title>
                        <div class="fq-panel-title">Fanout Query</div>
                        <div class="fq-panel-subtitle">The search the model derived from your prompt — verbatim</div>
                    </v-toolbar-title>
                    <v-spacer />
                    <v-btn icon @click="fanoutPanelOpen = false" aria-label="Close">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card-text class="pt-3">
                    <div class="d-flex align-start mb-2">
                        <v-icon class="mr-3" :color="selectedFanoutQuery.hasSiteOperator ? 'primary' : 'grey lighten-1'">
                            {{ selectedFanoutQuery.hasSiteOperator ? "mdi-web" : "mdi-magnify" }}
                        </v-icon>
                        <div class="fq-panel-name">{{ selectedFanoutQuery.query }}</div>
                    </div>
                    <div class="mb-3">
                        <v-chip v-for="p in selectedFanoutQuery.platforms || []" :key="p" x-small outlined class="mr-1">{{ p }}</v-chip>
                        <v-chip v-if="selectedFanoutQuery.hasSiteOperator" x-small color="teal lighten-5" text-color="teal darken-2">site: operator</v-chip>
                    </div>

                    <!-- Query direkt in Google nachvollziehen -->
                    <a class="fq-panel-link mb-3" :href="googleSearchUrl(selectedFanoutQuery.query)" target="_blank" rel="noopener">
                        <v-icon small class="mr-2" color="primary">mdi-google</v-icon>
                        <span class="fq-panel-link__text">Open this query in Google</span>
                        <v-icon x-small color="primary" class="ml-auto">mdi-open-in-new</v-icon>
                    </a>

                    <v-row dense class="mb-1">
                        <v-col cols="4">
                            <div class="fq-panel-stat">
                                <div class="fq-panel-stat__value">{{ selectedFanoutQuery.frequency != null ? selectedFanoutQuery.frequency + "%" : "–" }}</div>
                                <div class="fq-panel-stat__label">Frequency</div>
                            </div>
                        </v-col>
                        <v-col cols="4">
                            <div class="fq-panel-stat">
                                <div class="fq-panel-stat__value">{{ selectedFanoutQuery.runs }}<span class="fq-panel-stat__sub">/ {{ selectedFanoutQuery.promptRuns || "–" }}</span></div>
                                <div class="fq-panel-stat__label">Runs with query</div>
                            </div>
                        </v-col>
                        <v-col cols="4">
                            <div class="fq-panel-stat">
                                <div class="fq-panel-stat__value">{{ selectedFanoutQuery.dayCount }}d</div>
                                <div class="fq-panel-stat__label">Days seen</div>
                            </div>
                        </v-col>
                    </v-row>

                    <div class="fq-panel-section">
                        <div class="fq-panel-section__title"><v-icon x-small class="mr-1">mdi-message-text-outline</v-icon>Triggered by prompts</div>
                        <template v-if="(selectedFanoutQuery.prompts || []).length">
                            <div v-for="p in selectedFanoutQuery.prompts" :key="p" class="fq-panel-link mb-1" @click="openPromptFromFanoutQuery(p)">
                                <v-icon small class="mr-2" color="primary">mdi-message-text-outline</v-icon>
                                <span class="fq-panel-link__text">{{ p }}</span>
                                <v-icon x-small color="primary" class="ml-auto">mdi-open-in-new</v-icon>
                            </div>
                        </template>
                        <div v-else class="fq-panel-empty">No prompts recorded</div>
                    </div>

                    <div class="fq-panel-seen">
                        First seen {{ formatDate(selectedFanoutQuery.firstSeen) }} · Last seen {{ formatDate(selectedFanoutQuery.lastSeen) }} · seen on {{ selectedFanoutQuery.dayCount }}
                        {{ selectedFanoutQuery.dayCount === 1 ? "day" : "days" }}
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Einzelne Responses -->
        <v-card class="rounded_card quaroshadow fq-card mt-5">
            <v-toolbar flat class="gradient-toolbar elevation-0">
                <div class="d-flex align-center fq-toolbar__title">
                    <h2 class="project-data-heading__title">Responses</h2>
                    <v-tooltip top max-width="340">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>Individual prompt runs and the queries they produced. Click a row to open the full response.</span>
                    </v-tooltip>
                </div>
                <v-spacer />
                <v-text-field
                    v-model="responseSearch"
                    class="fq-table-search mr-3"
                    rounded
                    dense
                    filled
                    clearable
                    clear-icon="mdi-close"
                    prepend-inner-icon="mdi-magnify"
                    label="Search responses..."
                    single-line
                    hide-details />
                <v-btn rounded color="primary" :disabled="!total" @click="exportResponses" aria-label="Export responses">
                    <v-icon>mdi-cloud-download</v-icon>
                </v-btn>
            </v-toolbar>

            <div class="fanout-filters px-4 pt-3 pb-3">
                <v-select
                    v-model="filters.sourcesPresence"
                    :items="presenceFilterItems"
                    item-text="text"
                    item-value="value"
                    dense
                    outlined
                    rounded
                    hide-details
                    label="Present in Sources"
                    class="fanout-filter-control" />
                <v-select
                    v-model="filters.entitiesPresence"
                    :items="presenceFilterItems"
                    item-text="text"
                    item-value="value"
                    dense
                    outlined
                    rounded
                    hide-details
                    label="Presence in Entities"
                    class="fanout-filter-control" />
                <v-btn small text color="primary" class="fanout-filter-reset" @click="resetFilters">
                    <v-icon x-small class="mr-1">mdi-filter-remove-outline</v-icon>
                    Reset
                </v-btn>
            </div>

            <v-data-table
                :headers="headers"
                :items="rows"
                :loading="loading"
                class="elevation-0 fanout-table"
                fixed-header
                height="55vh"
                :server-items-length="total"
                :options.sync="options"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
                @update:options="onOptionsUpdate"
                @click:row="openPromptDetail">
                <template v-slot:header.platform="{ header }">
                    <span class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="260">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>AI platform of the response.</span>
                        </v-tooltip>
                    </span>
                </template>
                <template v-slot:header.prompt="{ header }">
                    <span class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="260">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Original prompt that produced this response.</span>
                        </v-tooltip>
                    </span>
                </template>
                <template v-slot:header.fanOutQueries="{ header }">
                    <span class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="320">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Combines <b>web_search_query</b> and <b>fan_out_queries</b> from the response.</span>
                        </v-tooltip>
                    </span>
                </template>
                <template v-slot:header.presentInSources="{ header }">
                    <span class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="320">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>"Yes" when the domain from <b>project.brand.brand_domain</b> is found in sources.</span>
                        </v-tooltip>
                    </span>
                </template>
                <template v-slot:header.presentInEntities="{ header }">
                    <span class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="320">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>"Yes" when <b>project.brand.brand_name</b> is detected in entities or mentions.</span>
                        </v-tooltip>
                    </span>
                </template>
                <template v-slot:header.importDate="{ header }">
                    <span class="fq-head-wrap">
                        <span>{{ header.text }}</span>
                        <v-tooltip top max-width="220">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Import date of the response.</span>
                        </v-tooltip>
                    </span>
                </template>

                <template v-slot:item.platform="{ item }">
                    <div class="fq-platform-cell">
                        <span class="fq-platform-icon-wrapper" v-if="getPlatformInfo(item.platform).logo">
                            <span class="fq-platform-icon" v-html="getPlatformInfo(item.platform).logo"></span>
                        </span>
                        <!-- <span>{{ getPlatformInfo(item.platform).label || item.platform || "—" }}</span> -->
                    </div>
                </template>

                <template v-slot:item.importDate="{ item }">
                    {{ formatDate(item.importDate) }}
                </template>

                <template v-slot:item.prompt="{ item }">
                    <div class="fq-prompt" :title="item.prompt || ''">{{ item.prompt || "—" }}</div>
                </template>

                <template v-slot:item.presentInSources="{ item }">
                    <v-chip x-small class="fq-presence-chip" :class="item.presentInSources ? 'fq-presence-chip--yes' : 'fq-presence-chip--no'" outlined>
                        <v-icon left x-small>{{ item.presentInSources ? "mdi-check-circle" : "mdi-close-circle" }}</v-icon>
                        {{ item.presentInSources ? "Yes" : "No" }}
                    </v-chip>
                </template>

                <template v-slot:item.presentInEntities="{ item }">
                    <v-chip x-small class="fq-presence-chip" :class="item.presentInEntities ? 'fq-presence-chip--yes' : 'fq-presence-chip--no'" outlined>
                        <v-icon left x-small>{{ item.presentInEntities ? "mdi-check-circle" : "mdi-close-circle" }}</v-icon>
                        {{ item.presentInEntities ? "Yes" : "No" }}
                    </v-chip>
                </template>

                <template v-slot:item.fanOutQueries="{ item }">
                    <div class="fq-query-list" v-if="item.combinedQueries.length">
                        <div v-for="(entry, idx) in item.combinedQueries" :key="`q-${item._id || item.task_id || idx}-${idx}`" class="fq-query-row" :title="entry.text">
                            <span class="fq-query-index">{{ idx + 1 }}</span>
                            <span class="fq-query-text">{{ entry.text }}</span>
                        </div>
                    </div>
                    <span v-else class="grey--text text--darken-1">—</span>
                </template>

                <template v-slot:no-data>
                    <div class="fanout-empty-state">
                        <v-icon color="grey" class="mb-2">mdi-database-search-outline</v-icon>
                        <div class="fanout-empty-state__title">No fanout queries found</div>
                        <div class="fanout-empty-state__text">No values in <b>web_search_query</b> or <b>fan_out_queries</b> match the current filters.</div>
                    </div>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
    export default {
        name: "AIProjectFanoutQueriesView",
        inject: {
            // Von AIProject bereitgestellt: öffnet das Prompt-Detail als Overlay statt per Route
            aiSuiteOpenPromptDetail: { default: null }
        },
        props: {
            projectId: { type: [String, Number], default: "" },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] },
            selectedDates: { type: Array, default: () => [] },
            selectedPlatform: { type: [Object, String], default: null },
            tags: { type: Array, default: () => [] },
            tagsOperator: { type: String, default: "or" },
            search: { type: String, default: "" },
            // Rohe Picker-Daten für die Query-Aggregation: selectedDates ist auf exakte
            // processed_dates gesnappt und taugt nicht als echter Zeitraum.
            dateRangeRaw: { type: Array, default: () => [] }
        },
        data() {
            return {
                loading: false,
                rowsRaw: [],
                total: 0,
                // Aggregierte Fanout-Queries + Keyword-Auswertung
                loadingQueries: false,
                queries: [],
                queriesTotal: 0,
                queryOptions: { page: 1, itemsPerPage: 10, sortBy: ["uses"], sortDesc: [true] },
                querySearch: "",
                queryKeywords: [],
                queryStats: { totalQueries: 0, recurringQueries: 0, withSiteOperator: 0, promptsWithQueries: 0, avgQueriesPerPrompt: 0 },
                activeKeyword: null,
                keywordsExpanded: false,
                quickFilter: "all",
                quickFilters: [
                    { value: "all", label: "All" },
                    { value: "recurring", label: "Recurring" },
                    { value: "site", label: "site:" }
                ],
                exportingQueries: false,
                queryReqSeq: 0,
                responseSearch: "",
                fanoutPanelOpen: false,
                selectedFanoutQuery: null,
                queryHeaders: [
                    { text: "Query", value: "query", width: "40%", tooltip: "The search query the model actually sent out, verbatim. A globe icon marks queries using a site: operator." },
                    { text: "Prompt", value: "prompts", sortable: false, width: 240, tooltip: "Your tracked prompt that produced this query. Queries are almost always specific to a single prompt." },
                    {
                        text: "Frequency",
                        value: "frequency",
                        align: "end",
                        width: 150,
                        tooltip:
                            "Share of this prompt's runs on the same platform in which this query appeared (runs with query ÷ runs of the prompt). 100% means the model used this exact wording every single time. Low values mean it rephrased the query on most runs."
                    },
                    {
                        text: "Days",
                        value: "dayCount",
                        align: "end",
                        width: 90,
                        tooltip:
                            "Number of distinct import days the query appeared on (with daily imports this equals the number of runs). Models often rephrase queries on every run, so queries seen on several days are the stable, reliable patterns."
                    },
                    { text: "Seen", value: "lastSeen", width: 130, tooltip: "First and last import date this query was observed within the selected range." }
                ],
                options: {
                    page: 1,
                    itemsPerPage: 25,
                    sortBy: ["importDate"],
                    sortDesc: [true]
                },
                filters: {
                    platform: "all",
                    sourcesPresence: "all",
                    entitiesPresence: "all",
                    queryType: "all"
                },
                headers: [
                    { text: "Platform", value: "platform" },
                    { text: "Prompt", value: "prompt", width: 320 },
                    { text: "Fanout Queries", value: "fanOutQueries", sortable: false },
                    { text: "Present in Sources", value: "presentInSources", width: 180, sortable: false },
                    { text: "Presence in Entities", value: "presentInEntities", width: 190, sortable: false },
                    { text: "Updated", value: "importDate", width: 160 }
                ]
            }
        },
        computed: {
            visibleKeywords() {
                return this.keywordsExpanded ? this.queryKeywords : this.queryKeywords.slice(0, 24)
            },
            kpiCards() {
                const s = this.queryStats
                return [
                    {
                        key: "total",
                        icon: "mdi-magnify-scan",
                        value: this.formatNum(s.totalQueries),
                        label: "Unique Queries",
                        hint: `across ${s.promptsWithQueries || 0} prompts`,
                        tooltip: "Distinct search queries the models derived from your prompts in this range. Identical wording counts once, no matter how often it was sent."
                    },
                    {
                        key: "avg",
                        icon: "mdi-call-split",
                        value: s.avgQueriesPerPrompt,
                        label: "Ø Queries per Prompt",
                        hint: s.maxQueriesPerPrompt ? `up to ${s.maxQueriesPerPrompt}` : "",
                        tooltip: "Average number of distinct queries a single prompt produced. Only prompts that generated at least one query are counted."
                    },
                    {
                        key: "recurring",
                        icon: "mdi-repeat",
                        value: this.formatNum(s.recurringQueries),
                        // Bezugsgröße mit anzeigen: ohne sie sieht "468" neben "468" nach einem
                        // Fehler aus, obwohl es schlicht heißt, dass jede Query wiederkehrt.
                        total: this.formatNum(s.totalQueries),
                        share: s.totalQueries ? Math.round((s.recurringQueries / s.totalQueries) * 100) : null,
                        label: "Recurring across days",
                        tooltip:
                            "Queries observed on more than one import day, out of all unique queries. Models usually rephrase their queries on every run — these repeat verbatim and are the reliable, targetable patterns. 100% means the model kept its wording constant for every query in this range."
                    },
                    {
                        key: "site",
                        icon: "mdi-web",
                        value: this.formatNum(s.withSiteOperator),
                        total: this.formatNum(s.totalQueries),
                        share: s.totalQueries ? Math.round((s.withSiteOperator / s.totalQueries) * 100) : null,
                        label: "Using site: operators",
                        tooltip:
                            'Queries containing a site: operator (e.g. "site:audi.de A3 Verbrauch"), out of all unique queries. The model deliberately restricted its search to one domain — often a competitor.'
                    }
                ]
            },
            queriesSubtitle() {
                const s = this.queryStats
                if (!s.totalQueries) return "Search queries the models derived from your prompts"
                return `Frequency = share of this prompt's runs where the query appeared · ${this.formatNum(this.queriesTotal)} of ${this.formatNum(s.totalQueries)} queries shown`
            },
            queryDateRange() {
                const dates = [...(this.dateRangeRaw || [])].filter(Boolean).sort()
                if (!dates.length) return null
                return { from: dates[0], to: dates[dates.length - 1] }
            },
            queryInputSignature() {
                return JSON.stringify({
                    projectId: this.projectId,
                    range: this.queryDateRange,
                    platform: this.selectedPlatform?.key || this.selectedPlatform,
                    tags: this.tags,
                    tagsOperator: this.tagsOperator
                })
            },
            rows() {
                const source = Array.isArray(this.rowsRaw) ? this.rowsRaw : []
                return source
                    .map((item, idx) => {
                        const webQueries = this.normalizeQueries(item?.web_search_query)
                        const fanOutQueries = this.normalizeQueries(item?.fan_out_queries)
                        return {
                            ...item,
                            _id: item?._id || `${item?.task_id || "task"}-${item?.importDate || "date"}-${idx}`,
                            webQueries,
                            fanOutQueries,
                            combinedQueries: [...webQueries.map((q) => ({ type: "web", text: q })), ...fanOutQueries.map((q) => ({ type: "fanout", text: q }))],
                            presentInSources: this.hasBrandDomainInSources(item),
                            presentInEntities: this.hasBrandNameInEntities(item),
                            _searchText: [item?.prompt || "", item?.platform || "", item?.responseText || "", webQueries.join(" "), fanOutQueries.join(" ")].join(" ").toLowerCase()
                        }
                    })
                    .filter((item) => item.webQueries.length || item.fanOutQueries.length)
            },
            platformFilterItems() {
                const options = [{ text: "All", value: "all" }]
                const projectKeys = Array.isArray(this.project?.platforms) ? this.project.platforms : []
                const rowKeys = Array.from(new Set((this.rows || []).map((row) => (row.platform || "").toString().toLowerCase()).filter(Boolean)))
                const keys = Array.from(new Set([...projectKeys.map((k) => (k || "").toString().toLowerCase()), ...rowKeys]))
                keys.sort().forEach((key) => {
                    options.push({ text: this.getPlatformInfo(key).label || key, value: key })
                })
                return options
            },
            presenceFilterItems() {
                return [
                    { text: "All", value: "all" },
                    { text: "Yes", value: "yes" },
                    { text: "No", value: "no" }
                ]
            },
            queryTypeFilterItems() {
                return [
                    { text: "All", value: "all" },
                    { text: "Web", value: "web" },
                    { text: "Fanout", value: "fanout" }
                ]
            }
        },
        watch: {
            projectId: {
                immediate: true,
                handler() {
                    this.fetchFanoutRows()
                }
            },
            selectedDates: {
                deep: true,
                handler() {
                    this.fetchFanoutRows()
                }
            },
            selectedPlatform: {
                deep: true,
                handler() {
                    this.fetchFanoutRows()
                }
            },
            tags: {
                deep: true,
                handler() {
                    this.fetchFanoutRows()
                }
            },
            tagsOperator() {
                this.fetchFanoutRows()
            },
            search() {
                this.options.page = 1
                this.fetchFanoutRows()
            },
            responseSearch() {
                clearTimeout(this._responseSearchDebounce)
                this._responseSearchDebounce = setTimeout(() => {
                    this.options.page = 1
                    this.fetchFanoutRows()
                }, 400)
            },
            filters: {
                deep: true,
                handler() {
                    this.options.page = 1
                    this.fetchFanoutRows()
                }
            },
            queryInputSignature: {
                immediate: true,
                handler() {
                    this.queryOptions.page = 1
                    this.fetchQueries()
                }
            },
            queryOptions: {
                deep: true,
                handler() {
                    this.fetchQueries()
                }
            },
            querySearch() {
                clearTimeout(this._querySearchDebounce)
                this._querySearchDebounce = setTimeout(() => {
                    this.activeKeyword = null
                    this.queryOptions.page = 1
                    this.fetchQueries()
                }, 400)
            }
        },
        methods: {
            // ---- Aggregierte Fanout-Queries + Keywords ----
            resolveApiRoot() {
                return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                    this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                ) == -1
                    ? process.env.VUE_APP_MONITORING_API_URL
                    : process.env.VUE_APP_APIURL + "/monitoring-api"
            },
            queryPayloadBase() {
                const payload = {
                    company_id: localStorage.company_id,
                    project_id: this.projectId || this.$route.params.id
                }
                const platformKey = this.selectedPlatform?.key || this.selectedPlatform
                if (platformKey && platformKey !== "all") payload.platform = platformKey
                if (this.queryDateRange) payload.importDateRange = { from: this.queryDateRange.from, to: this.queryDateRange.to }
                if (this.tags && this.tags.length) {
                    payload.tags = this.tags
                    payload.tagsOperator = this.tagsOperator
                }
                return payload
            },
            async fetchQueries() {
                if (!this.queryDateRange) return
                const seq = ++this.queryReqSeq
                this.loadingQueries = true
                try {
                    const sortField = this.queryOptions.sortBy?.[0] || "uses"
                    const sortDir = this.queryOptions.sortDesc?.[0] === false ? 1 : -1
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/fanout/queries", {
                        ...this.queryPayloadBase(),
                        skip: (this.queryOptions.page - 1) * this.queryOptions.itemsPerPage,
                        limit: this.queryOptions.itemsPerPage,
                        querySearch: this.activeKeyword || this.querySearch || undefined,
                        recurringOnly: this.quickFilter === "recurring" || undefined,
                        siteOnly: this.quickFilter === "site" || undefined,
                        sort: { [sortField]: sortDir }
                    })
                    if (seq !== this.queryReqSeq) return
                    this.queries = Array.isArray(resp.data?.queries) ? resp.data.queries : []
                    this.queriesTotal = resp.data?.total || 0
                    this.queryKeywords = Array.isArray(resp.data?.keywords) ? resp.data.keywords : []
                    if (resp.data?.stats) this.queryStats = resp.data.stats
                } catch (e) {
                    console.error("fanout/queries failed", e)
                } finally {
                    if (seq === this.queryReqSeq) this.loadingQueries = false
                }
            },
            formatNum(v) {
                return Number(v || 0).toLocaleString("de-DE")
            },
            frequencyColor(freq) {
                if (freq >= 75) return "primary"
                if (freq >= 30) return "teal lighten-2"
                return "grey lighten-1"
            },
            onQuickFilterChange() {
                if (!this.quickFilter) this.quickFilter = "all"
                this.queryOptions.page = 1
                this.fetchQueries()
            },
            keywordTier(kw) {
                const total = this.queryStats.totalQueries || 1
                const share = (kw.queryCount / total) * 100
                if (share >= 10) return "fq-kw-chip--lg"
                if (share >= 4) return "fq-kw-chip--md"
                return "fq-kw-chip--sm"
            },
            toggleKeyword(term) {
                this.activeKeyword = this.activeKeyword === term ? null : term
                this.querySearch = ""
                this.queryOptions.page = 1
                this.fetchQueries()
            },
            downloadCsvFile(filename, headers, rows) {
                const esc = (v) => {
                    const s = v == null ? "" : String(v)
                    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
                }
                const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n")
                const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(link.href)
            },
            exportKeywords() {
                this.downloadCsvFile(
                    "fanout_query_keywords",
                    ["Term", "Queries Containing Term", "Share %"],
                    this.queryKeywords.map((k) => [k.term, k.queryCount, this.queryStats.totalQueries ? ((k.queryCount / this.queryStats.totalQueries) * 100).toFixed(1) : ""])
                )
            },
            async exportQueries() {
                this.exportingQueries = true
                try {
                    const sortField = this.queryOptions.sortBy?.[0] || "uses"
                    const sortDir = this.queryOptions.sortDesc?.[0] === false ? 1 : -1
                    const resp = await this.$helpers.axiosPost(this.resolveApiRoot() + "/ai-suite/fanout/queries", {
                        ...this.queryPayloadBase(),
                        skip: 0,
                        limit: 1000,
                        querySearch: this.activeKeyword || this.querySearch || undefined,
                        sort: { [sortField]: sortDir }
                    })
                    const rows = Array.isArray(resp.data?.queries) ? resp.data.queries : []
                    this.downloadCsvFile(
                        "fanout_queries",
                        ["Query", "Prompts", "Frequency %", "Runs With Query", "Prompt Runs", "Uses", "Days Seen", "Site Operator", "First Seen", "Last Seen"],
                        rows.map((q) => [
                            q.query,
                            (q.prompts || []).join(" | "),
                            q.frequency != null ? q.frequency : "",
                            q.runs,
                            q.promptRuns,
                            q.uses,
                            q.dayCount,
                            q.hasSiteOperator ? "yes" : "no",
                            this.formatDate(q.firstSeen),
                            this.formatDate(q.lastSeen)
                        ])
                    )
                } catch (e) {
                    console.error("fanout queries export failed", e)
                } finally {
                    this.exportingQueries = false
                }
            },
            resolveApiBase() {
                return (
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/promptresponses/get"
                )
            },
            normalizeQueries(value) {
                if (!Array.isArray(value)) return []
                return value.map((q) => (q == null ? "" : String(q).trim())).filter(Boolean)
            },
            normalizeDomain(value) {
                const raw = (value || "").toString().trim().toLowerCase()
                if (!raw) return ""
                return raw
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split("/")[0]
            },
            normalizeToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            getBrandDomain() {
                return this.normalizeDomain(this.project?.brand?.brand_domain || "")
            },
            getBrandNameToken() {
                return this.normalizeToken(this.project?.brand?.brand_name || "")
            },
            sourceDomainFromEntry(src) {
                if (!src) return ""
                if (typeof src === "string") return this.normalizeDomain(src)
                return this.normalizeDomain(src.domain || src.url || src.key || "")
            },
            hasBrandDomainInSources(item) {
                const brandDomain = this.getBrandDomain()
                if (!brandDomain) return false
                const sources = Array.isArray(item?.sources) ? item.sources : []
                return sources.some((src) => {
                    const d = this.sourceDomainFromEntry(src)
                    if (!d) return false
                    return d.includes(brandDomain)
                })
            },
            entityMentionValues(entity) {
                const mentions = Array.isArray(entity?.mentions) ? entity.mentions : []
                return mentions.map((m) => (typeof m === "string" ? m : m?.text?.content || m?.content || m?.name || "")).filter(Boolean)
            },
            hasBrandNameInEntities(item) {
                const brandName = this.getBrandNameToken()
                if (!brandName) return false

                const entities = Array.isArray(item?.mergedEntities) ? item.mergedEntities : Array.isArray(item?.entities) ? item.entities : []

                return entities.some((entity) => {
                    const candidates = [entity?.canonicalName, entity?.name, entity?.brand_name, ...this.entityMentionValues(entity)]
                    return candidates.some((val) => {
                        const token = this.normalizeToken(val)
                        return token && (token.includes(brandName) || brandName.includes(token))
                    })
                })
            },
            formatDate(value) {
                if (!value) return "—"
                const d = new Date(value)
                if (isNaN(d)) return "—"
                return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`
            },
            getPlatformInfo(name) {
                let cursor = this.$parent
                while (cursor) {
                    if (typeof cursor.getPlatformInfo === "function") {
                        try {
                            const info = cursor.getPlatformInfo(name)
                            if (info && (info.logo || info.label)) return info
                        } catch (e) {
                            // ignore
                        }
                    }
                    cursor = cursor.$parent
                }
                return { logo: null, label: name || "—" }
            },
            // ---- Query-Detail-Panel ----
            googleSearchUrl(query) {
                return "https://www.google.com/search?q=" + encodeURIComponent(query || "")
            },
            openFanoutQueryPanel(row) {
                this.selectedFanoutQuery = row
                this.fanoutPanelOpen = true
            },
            // Prompt im Panel angeklickt -> PromptOverview am letzten Sichtungstag der Query
            openPromptFromFanoutQuery(prompt) {
                if (!prompt) return
                const projectId = this.project?._id || this.projectId || this.$route?.params?.id
                if (!projectId) return
                this.fanoutPanelOpen = false

                const rawDate = this.selectedFanoutQuery?.lastSeen
                const parsed = rawDate ? new Date(rawDate) : null
                const dateKey = parsed && !isNaN(parsed) ? parsed.toISOString().slice(0, 10) : null
                const platform = (this.selectedFanoutQuery?.platforms || [])[0]

                const query = {
                    prompt,
                    platform: platform || undefined,
                    dates: dateKey ? JSON.stringify([dateKey, dateKey]) : undefined
                }

                // Bevorzugt als Overlay im AIProject öffnen (kein Routenwechsel, View bleibt erhalten)
                if (this.aiSuiteOpenPromptDetail) {
                    this.aiSuiteOpenPromptDetail({ ...query, project_id: projectId })
                    return
                }

                try {
                    sessionStorage.setItem(`ai_suite_project_return_section_${projectId}`, "fanout")
                } catch (e) {
                    // ignore storage errors
                }

                this.$router.push({
                    name: "PromptDetail",
                    params: { id: projectId },
                    query
                })
            },
            openPromptDetail(item) {
                if (!item) return
                const projectId = this.project?._id || this.projectId || this.$route?.params?.id
                if (!projectId) return

                const query = {}
                if (item.task_id || item.taskId) query.task_id = item.task_id || item.taskId
                if (item.prompt) query.prompt = item.prompt
                if (item.platform) query.platform = item.platform
                const routeBrand = this.getRouteBrandPayload()
                if (routeBrand) query.brand = routeBrand

                const dateRange = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                if (dateRange.length) query.dates = JSON.stringify(dateRange)

                // Bevorzugt als Overlay im AIProject öffnen (kein Routenwechsel, View bleibt erhalten)
                if (this.aiSuiteOpenPromptDetail) {
                    this.aiSuiteOpenPromptDetail({ ...query, project_id: projectId })
                    return
                }

                this.$router.push({
                    name: "PromptDetail",
                    params: { id: projectId },
                    query
                })
            },
            normalizeToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            findMatchedBrandByProjectName() {
                const projectBrandName = this.project?.brand?.brand_name || ""
                const projectToken = this.normalizeToken(projectBrandName)
                if (!projectToken || !Array.isArray(this.brands) || !this.brands.length) return null

                const getMentionValue = (mention) => {
                    if (mention == null) return ""
                    if (typeof mention === "string" || typeof mention === "number") return String(mention)
                    return mention?.text?.content || mention?.content || mention?.canonicalName || mention?.name || mention?.brand || mention?.entity || ""
                }

                return (
                    this.brands.find((entry) => {
                        const mentions = Array.isArray(entry?.mentions) ? entry.mentions : []
                        return mentions.some((mention) => this.normalizeToken(getMentionValue(mention)) === projectToken)
                    }) || null
                )
            },
            getRouteBrandPayload() {
                const matched = this.findMatchedBrandByProjectName()
                if (!matched) return null

                try {
                    return JSON.stringify({
                        canonicalName: matched.canonicalName || matched.brand_name || matched.brand || matched.name || null,
                        brand_name: matched.brand_name || matched.brand || matched.canonicalName || matched.name || null,
                        brand: matched.brand || matched.brand_name || matched.canonicalName || matched.name || null,
                        mentions: Array.isArray(matched.mentions) ? matched.mentions : []
                    })
                } catch (e) {
                    return null
                }
            },
            csvEscape(value) {
                if (value === null || value === undefined) return ""
                const str = String(value)
                if (/[",\n;]/.test(str)) return `"${str.replace(/"/g, '""')}"`
                return str
            },
            // Eigener Export-Button, seit der Tab nicht mehr im generischen Card-Wrapper liegt
            async exportResponses() {
                const csv = await this.exportTableData()
                if (!csv) return
                const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.download = `fanout_responses_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(link.href)
            },
            async exportTableData() {
                const header = ["Platform", "Prompt", "Present in Sources", "Presence in Entities", "Fanout Queries", "Import Date", "Task ID"]
                const lines = [header.join(",")]
                this.rows.forEach((row) => {
                    const combined = (row.combinedQueries || []).map((q) => `[${q.type}] ${q.text}`).join(" | ")
                    lines.push(
                        [
                            this.csvEscape(this.getPlatformInfo(row.platform).label || row.platform || ""),
                            this.csvEscape(row.prompt || ""),
                            this.csvEscape(row.presentInSources ? "Yes" : "No"),
                            this.csvEscape(row.presentInEntities ? "Yes" : "No"),
                            this.csvEscape(combined),
                            this.csvEscape(this.formatDate(row.importDate)),
                            this.csvEscape(row.task_id || row.taskId || "")
                        ].join(",")
                    )
                })
                return lines.join("\n")
            },
            resetFilters() {
                this.filters = {
                    platform: "all",
                    sourcesPresence: "all",
                    entitiesPresence: "all",
                    queryType: "all"
                }
            },
            onOptionsUpdate(newOptions) {
                this.options = newOptions
                this.fetchFanoutRows()
            },
            async fetchFanoutRows() {
                const projectId = this.projectId || this.$route?.params?.id
                if (!projectId || !Array.isArray(this.selectedDates) || !this.selectedDates.length) {
                    this.rowsRaw = []
                    this.total = 0
                    return
                }

                this.loading = true
                const payload = {
                    company_id: localStorage.company_id,
                    project_id: projectId,
                    selectedDates: this.selectedDates,
                    requireFanoutQueries: true,
                    search: this.responseSearch || this.search || "",
                    sort: {
                        [this.options.sortBy?.[0] || "importDate"]: this.options.sortDesc?.[0] ? -1 : 1
                    },
                    skip: ((this.options.page || 1) - 1) * (this.options.itemsPerPage || 25),
                    limit: this.options.itemsPerPage || 25
                }

                if (this.filters.platform !== "all") {
                    payload.platform = this.filters.platform
                } else if (this.selectedPlatform && this.selectedPlatform.key && this.selectedPlatform.key !== "all") {
                    payload.platform = this.selectedPlatform.key
                }
                if (this.tags && this.tags.length) {
                    payload.tags = this.tags
                    payload.tagsOperator = this.tagsOperator
                }
                if (this.filters.queryType !== "all") payload.fanoutQueryType = this.filters.queryType
                if (this.filters.sourcesPresence !== "all") {
                    payload.sourcesPresence = this.filters.sourcesPresence
                    payload.sourcesPresenceDomain = this.project?.brand?.brand_domain || ""
                }
                if (this.filters.entitiesPresence !== "all") {
                    payload.entitiesPresence = this.filters.entitiesPresence
                    payload.entitiesPresenceName = this.project?.brand?.brand_name || ""
                }

                try {
                    const resp = await this.$helpers.axiosPost(this.resolveApiBase(), payload)
                    this.rowsRaw = Array.isArray(resp?.data?.responses) ? resp.data.responses : []
                    this.total = Number(resp?.data?.total || 0)
                } catch (e) {
                    this.rowsRaw = []
                    this.total = 0
                } finally {
                    this.loading = false
                }
            }
        }
    }
</script>

<style scoped>
    .fanout-filters {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    }
    .fanout-filter-control {
        min-width: 160px;
        max-width: 220px;
    }
    .fanout-filter-reset {
        text-transform: none;
        font-weight: 600;
    }

    .fanout-table {
        cursor: pointer;
    }
    /* Kopfzeilen wie in der Prompts-Tabelle: leicht und luftig statt fett */
    .fanout-table >>> thead th,
    .fq-query-table >>> thead th {
        white-space: nowrap !important;
        text-transform: uppercase !important;
        font-size: 12px !important;
        letter-spacing: 0.1px !important;
        font-weight: 300 !important;
    }
    .fq-query-table >>> tbody td {
        padding: 12px 16px !important;
        font-size: 14px;
    }
    .fanout-table >>> tbody tr {
        transition: background-color 0.16s ease;
    }
    .fanout-table >>> tbody tr:hover {
        background: rgba(25, 118, 210, 0.04) !important;
    }
    .fanout-table >>> .v-data-table__wrapper td {
        padding-top: 11px !important;
        padding-bottom: 11px !important;
    }

    .fq-platform-cell {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .fq-platform-icon-wrapper {
        width: 30px;
        height: 30px;
        border-radius: 10px;
        background: #f8fafc;
        border: 1px solid rgba(15, 23, 42, 0.08);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex: 0 0 auto;
    }
    .fq-platform-icon {
        width: 18px;
        height: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
    }
    .fq-platform-icon >>> svg {
        width: 18px;
        height: 18px;
        display: block;
    }
    .fq-prompt {
        max-width: 320px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.35;
        font-weight: 600;
        color: rgba(15, 23, 42, 0.84);
    }
    .fq-query-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 6px 0;
    }
    .fq-presence-chip {
        font-weight: 700;
        letter-spacing: 0.1px;
    }
    .fq-presence-chip--yes {
        background: rgba(34, 197, 94, 0.08);
        border-color: rgba(34, 197, 94, 0.34) !important;
        color: #15803d !important;
    }
    .fq-presence-chip--no {
        background: rgba(239, 68, 68, 0.08);
        border-color: rgba(239, 68, 68, 0.34) !important;
        color: #b91c1c !important;
    }
    .fq-query-row {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        background: rgba(25, 118, 210, 0.04);
        border: 1px solid rgba(25, 118, 210, 0.12);
        border-radius: 8px;
        padding: 4px 7px;
    }
    .fq-query-index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        border-radius: 999px;
        background: rgba(25, 118, 210, 0.08);
        color: var(--v-primary-base);
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        margin-top: 1px;
        flex: 0 0 auto;
    }
    .fq-query-text {
        line-height: 1.35;
        color: rgba(15, 23, 42, 0.85);
        word-break: break-word;
    }
    .fq-query-type {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.2px;
        border-radius: 999px;
        padding: 1px 6px;
        line-height: 1.5;
        margin-top: 1px;
        flex: 0 0 auto;
    }
    .fq-query-type--web {
        background: rgba(14, 165, 233, 0.16);
        color: #0369a1;
    }
    .fq-query-type--fanout {
        background: rgba(139, 92, 246, 0.16);
        color: #6d28d9;
    }

    .fanout-empty-state {
        padding: 28px 16px;
        text-align: center;
        color: rgba(15, 23, 42, 0.7);
    }
    .fanout-empty-state__title {
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 4px;
    }
    .fanout-empty-state__text {
        font-size: 12px;
        max-width: 560px;
        margin: 0 auto;
        line-height: 1.4;
    }

    .theme--dark .fanout-table >>> tbody tr:hover {
        background: rgba(66, 165, 245, 0.12) !important;
    }
    .theme--dark .fanout-filters {
        border-bottom-color: rgba(255, 255, 255, 0.12);
    }
    .theme--dark .fq-platform-icon-wrapper {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.14);
    }
    .theme--dark .fq-prompt,
    .theme--dark .fq-query-text {
        color: rgba(241, 245, 249, 0.92);
    }
    .theme--dark .fq-query-type--web {
        background: rgba(14, 165, 233, 0.28);
        color: #7dd3fc;
    }
    .theme--dark .fq-query-type--fanout {
        background: rgba(139, 92, 246, 0.32);
        color: #c4b5fd;
    }
    .theme--dark .fq-query-row {
        background: rgba(66, 165, 245, 0.12);
        border-color: rgba(66, 165, 245, 0.28);
    }
    .theme--dark .fq-presence-chip--yes {
        background: rgba(34, 197, 94, 0.2);
        border-color: rgba(34, 197, 94, 0.46) !important;
        color: #86efac !important;
    }
    .theme--dark .fq-presence-chip--no {
        background: rgba(239, 68, 68, 0.2);
        border-color: rgba(239, 68, 68, 0.46) !important;
        color: #fca5a5 !important;
    }
    .theme--dark .fanout-empty-state {
        color: rgba(226, 232, 240, 0.8);
    }

    .fq-head-wrap {
        display: inline-flex;
        align-items: center;
    }
    /* Aggregierte Fanout-Queries + Keyword-Wolke */
    /* KPI-Karten: Label oben, Zahl groß darunter, Anteil als Balken */
    .fq-kpi {
        border-top: 3px solid var(--v-primary-base);
    }
    .fq-kpi__head {
        display: flex;
        align-items: center;
        margin-bottom: 6px;
    }
    .fq-kpi__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: rgba(0, 160, 137, 0.1);
        margin-right: 8px;
        flex: 0 0 24px;
    }
    .fq-kpi__value {
        font-size: 1.7rem;
        font-weight: 600;
        line-height: 1.1;
        font-variant-numeric: tabular-nums;
    }
    .fq-kpi__label {
        display: inline-flex;
        align-items: center;
        font-size: 0.74rem;
        font-weight: 600;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: #8a94a6;
    }
    .fq-kpi__total {
        font-size: 0.85rem;
        font-weight: 400;
        color: #8a94a6;
        margin-left: 4px;
    }
    .fq-kpi__bar-row {
        display: flex;
        align-items: center;
        margin-top: 8px;
        min-height: 16px;
    }
    .fq-kpi__hint {
        font-size: 0.72rem;
        color: #b0b8c4;
    }
    .fq-kpi__bar {
        flex: 1 1 auto;
        margin-right: 8px;
    }
    .fq-kpi__share {
        font-size: 0.72rem;
        font-weight: 600;
        color: var(--v-primary-base);
        font-variant-numeric: tabular-nums;
        flex: 0 0 auto;
    }
    /* Karten mit Toolbar-Kopf, identisch zum Prompts-Tab */
    .fq-card {
        border-top: 4px solid var(--v-primary-base);
        overflow: hidden;
    }
    .fq-card__body {
        padding: 16px 20px 20px;
    }
    .fq-toolbar__title {
        min-width: 0;
        flex: 0 1 auto;
    }
    .fq-toolbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        row-gap: 8px;
    }
    .fq-head-block {
        min-width: 240px;
        margin-right: 16px;
    }
    .fq-quick-filter {
        flex: 0 0 auto;
    }
    .fq-quick-filter >>> .v-slide-group__content {
        padding: 0;
    }
    .fq-quick-filter >>> .v-chip {
        margin: 0 4px 0 0;
    }
    .fq-query-table >>> tbody tr {
        cursor: pointer;
    }
    /* Query-Detail-Panel (Inhalt; die Dialog-Hülle kommt global aus .shopping-item-dialog) */
    .fq-panel-toolbar {
        border-bottom: 1px solid #eef1f5;
    }
    .fq-panel-title {
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.3;
    }
    .fq-panel-subtitle {
        font-size: 0.72rem;
        color: #8a94a6;
    }
    .fq-panel-name {
        font-size: 0.9rem;
        font-weight: 600;
        line-height: 1.35;
        word-break: break-word;
    }
    .fq-panel-stat {
        text-align: center;
        background: #f7f8fa;
        border-radius: 8px;
        padding: 8px 4px;
    }
    .fq-panel-stat__value {
        font-size: 1rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
    }
    .fq-panel-stat__sub {
        font-size: 0.72rem;
        font-weight: 400;
        color: #8a94a6;
        margin-left: 2px;
    }
    .fq-panel-stat__label {
        font-size: 0.68rem;
        color: #8a94a6;
    }
    .fq-panel-section {
        margin-top: 14px;
    }
    .fq-panel-section__title {
        display: flex;
        align-items: center;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #5b6472;
        margin-bottom: 6px;
    }
    /* Klickbare Aktionszeilen (Google-Link, Prompt-Links) — sichtbar als Aktionen hervorgehoben */
    .fq-panel-link {
        display: flex;
        align-items: center;
        font-size: 0.8rem;
        padding: 8px 10px;
        border: 1px solid rgba(0, 160, 137, 0.25);
        border-radius: 8px;
        background: rgba(0, 160, 137, 0.04);
        cursor: pointer;
        color: inherit;
        text-decoration: none;
        transition: background 0.15s ease, border-color 0.15s ease;
    }
    .fq-panel-link:hover {
        background: rgba(0, 160, 137, 0.1);
        border-color: rgba(0, 160, 137, 0.5);
    }
    .fq-panel-link__text {
        flex: 1 1 auto;
        min-width: 0;
        font-weight: 500;
    }
    .theme--dark .fq-panel-link {
        background: rgba(0, 160, 137, 0.1);
        border-color: rgba(0, 160, 137, 0.35);
    }
    .theme--dark .fq-panel-link:hover {
        background: rgba(0, 160, 137, 0.18);
    }
    .fq-panel-empty {
        font-size: 0.75rem;
        color: #b0b8c4;
    }
    .fq-panel-seen {
        margin-top: 16px;
        font-size: 0.72rem;
        color: #8a94a6;
    }
    .theme--dark .fq-panel-stat {
        background: #23272e;
    }
    .fq-days {
        font-size: 0.75rem;
        font-variant-numeric: tabular-nums;
        color: #b0b8c4;
    }
    .fq-days--multi {
        color: #5b6472;
        font-weight: 600;
    }
    .fq-freq-value {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        min-width: 42px;
        text-align: right;
    }
    .fq-section__title {
        font-size: 1rem;
        font-weight: 600;
    }
    .fq-section__subtitle {
        font-size: 0.75rem;
        color: #8a94a6;
    }
    .fq-empty {
        font-size: 0.8rem;
        color: #b0b8c4;
        padding: 6px 0;
    }
    .fq-kw-chip {
        margin: 0 4px 6px 0;
        cursor: pointer;
    }
    .fq-kw-chip--lg {
        font-size: 0.85rem;
        font-weight: 600;
    }
    .fq-kw-chip--md {
        font-size: 0.78rem;
    }
    .fq-kw-chip--sm {
        font-size: 0.72rem;
    }
    .fq-kw-chip__count {
        opacity: 0.6;
        margin-left: 5px;
        font-variant-numeric: tabular-nums;
    }
    .fq-table-search {
        max-width: 260px;
        flex: 0 1 260px;
    }
    /* Suchfeld in der Toolbar: kein zusätzlicher Innenabstand nach unten */
    .gradient-toolbar .fq-table-search >>> .v-input__slot {
        margin-bottom: 0;
    }
    .fq-switch >>> .v-label {
        font-size: 0.78rem;
        color: #8a94a6;
    }
    /* Query-Text darf breit laufen; abgeschnitten wird erst bei sehr langen Queries */
    .fq-query-cell {
        display: inline-block;
        max-width: 620px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }
    .fq-prompt-cell {
        display: inline-block;
        max-width: 260px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
        font-size: 0.78rem;
        color: #5b6472;
    }
    .theme--dark .fq-prompt-cell,
    .theme--dark .fq-section__subtitle,
    .theme--dark .fq-kpi__label,
    .theme--dark .fq-kpi__total {
        color: #a8b1bd;
    }
    .theme--dark .fq-days--multi {
        color: #dfe3e8;
    }
    .theme--dark .fq-kpi__icon {
        background: rgba(0, 160, 137, 0.18);
    }
    .fq-date-cell {
        font-size: 0.75rem;
        white-space: nowrap;
    }
</style>
